/**
 * Installs a global sanitization layer over the `sonner` toast object.
 * Call once at app startup. Wraps `.error()` (and the base callable when used
 * in destructive contexts) so any string that looks like a leaked backend
 * error is replaced with a generic, user-friendly message.
 *
 * Sonner's `toast` is exported as a callable function with methods bolted on
 * (`.error`, `.success`, etc.). The methods are mutable own properties, so
 * we can monkey-patch them safely without forcing all 90+ call sites to
 * change their imports.
 *
 * Raw error details remain in `console.error` for debugging.
 */
import { toast } from "sonner";
import { looksLikeBackendLeak, ERROR_MESSAGES } from "@/lib/errorHandler";

type ToastFn = (...args: any[]) => any;

let installed = false;

function laundered(message: unknown): string {
  if (typeof message !== "string") {
    return ERROR_MESSAGES.default;
  }
  if (looksLikeBackendLeak(message)) {
    return ERROR_MESSAGES.default;
  }
  return message;
}

function launderOpts(opts: any): any {
  if (!opts || typeof opts !== "object") return opts;
  const out = { ...opts };
  if (typeof out.description === "string" && looksLikeBackendLeak(out.description)) {
    out.description = ERROR_MESSAGES.default;
  }
  return out;
}

export function installSafeSonner() {
  if (installed) return;
  installed = true;
  const t = toast as unknown as Record<string, ToastFn> & ToastFn;

  const origError = t.error?.bind(toast);
  if (origError) {
    t.error = (msg: unknown, opts?: any) => origError(laundered(msg), launderOpts(opts));
  }
  // warning is sometimes used for failure-ish signals — also launder description
  const origWarning = t.warning?.bind(toast);
  if (origWarning) {
    t.warning = (msg: unknown, opts?: any) => {
      // warning titles are often safe; only scrub if they clearly leak
      const safeMsg =
        typeof msg === "string" && looksLikeBackendLeak(msg) ? ERROR_MESSAGES.default : msg;
      return origWarning(safeMsg, launderOpts(opts));
    };
  }
}
